/**
 * Os quatro módulos de "outros projetos": torques, relés e fusíveis, tabelas de
 * valores e planos de manutenção.
 *
 * Os quatro têm a mesma arquitetura — um registro **mestre** com uma ou duas
 * coleções **filhas** — e é por isso que dá para atendê-los com um punhado de
 * hooks genéricos em vez de quatro clientes parecidos.
 *
 * **Volume medido, e onde ele dói.** Torques 6.603 (com 11.077 etapas), relés
 * 401 projetos (7.117 caixas, 25.863 itens de tabela), tabelas de valores 4.859
 * (18.417 linhas) e planos 208 — mas os **grupos de manutenção guardam 582.249
 * itens** em 1.222 grupos. O pior grupo tem **15.283 itens e devolve 3,9 MB numa
 * resposta só**, porque essa rota não pagina. A tela precisa saber disso antes
 * de desenhar, e sabe.
 *
 * **Tudo aqui é dado global**: não pertence a projeto de pinout, então a trava
 * recusa gravação em desenvolvimento e as telas avisam antes.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { api, foraDoProjeto, obter } from "./cliente"

// ------------------------------------------------------------------ tipos

export type Torque = {
  id: number
  titulo: string
  texto: string
  restricao: string
  motorId: number
  sistemaId: number
  anoInicial: number
  anoFinal: number
  quantidadeParafusos: number
  publicado: boolean
  dataCriacao: string
}

export type EtapaDoTorque = {
  id: number
  etapa: number
  texto: string
  restricao: string
  grandezaId: number
}

export type ImagemDoTorque = {
  id: number
  bancoTorqueImgId?: number
  /**
   * O nome do arquivo. **A API chama `imagem`**, e este tipo dizia `arquivo` — a coluna
   * da tabela aparecia sempre vazia, sem erro nenhum. `arquivo` fica como opcional
   * porque nada garante que nenhuma rota antiga o devolva.
   */
  imagem?: string
  arquivo?: string
  ordem?: number
}

/** Uma figura do acervo compartilhado de torques: 41 desenhos servindo 395 torques. */
export type FiguraDoAcervo = {
  id: number
  imagem: string
  titulo: string
}

export type ProjetoDeReles = {
  id: number
  titulo: string
  publicado: boolean
  publicadoEm: string
  /** Última mudança no projeto ou dentro dele; nula nos que ninguém mexeu desde a 2.8.0 da API. */
  alteradoEm?: string | null
}

export type CaixaDeReles = {
  id: number
  projetoId: number
  titulo: string
  versao: string
  veiculoId: number
  motorId: number
  sistemaId: number
  ano: number
  identificadores: string
}

export type ItemDeReles = {
  id: number
  identificador: string
  designacao: string
  local: string
  definicao: string
  corrente: string
  funcao: string
  componente: string
  terminal: string
  cor: string
  restricao: string
}

/**
 * Quantos caracteres cada campo de uma linha de relés aceita — o mesmo teto da ApiSistema
 * (`RelesFusiveisTabela.Limites`). Acima disso a API responde 400; antes da 2.9.1 da API era
 * o banco que recusava, e a tela via um 500 mudo.
 */
export const TETO_DO_ITEM_DE_RELES: Record<keyof Omit<ItemDeReles, "id">, number> = {
  identificador: 100, designacao: 100, local: 100, definicao: 100, corrente: 100,
  funcao: 2000, componente: 100, terminal: 100, cor: 100, restricao: 255,
}

export type TabelaDeValores = {
  id: number
  titulo: string
  tipo: number
  texto: string
  qtdColunas: number
  sistemaId: number
  data: string
  publicado: boolean
  publicadoData: string
  colunas: { coluna: number; titulo?: string; grandeza?: string; tipoCampo: number; tolerancia: number }[]
}

export type LinhaDaTabela = { id: number; ordem: number; valores: (string | null)[] }

export type VeiculoVinculado = {
  id: number
  veiculoId: number
  veiculoModelo: string
  motorId: number
  motorNome: string
  /**
   * **Número no plano, data na tabela de valores.** O plano grava o ano (`2022`; 0 é
   * sem limite); a tabela grava `date` — `2022-01-01` no início e `2022-12-31` no fim,
   * que é a convenção do acervo — e pode vir `null`.
   */
  anoInicial: number | string | null
  anoFinal: number | string | null
}

export type Plano = {
  id: number
  titulo: string
  montadora: boolean
  manual: string
  pagina: string
  migrado: boolean
  data: string
  publicado: boolean
  publicadoEm: string
}

export type TextoDePlano = {
  id: number
  texto: string
  validado: boolean
  setor: string
  ordem: number
  assuntoId: number
  sistemaId: number
}

export type ConteudoDoPlano = {
  id: number
  planoManutencaoTextoId: number
  texto: string
  setor: string
  km: number
  tempo: number
  repetir: boolean
  kmRepeticao: number
  severa: boolean
  obs: string
}

export type GrupoDeManutencao = {
  id: number
  veiculoId: number
  versao: number
  publicado: boolean
  publicadoEm: string
}

export type ItemDeManutencao = {
  id: number
  grupoId: number
  motorId: number
  ano: number
  quilometragem: number
  tempoMeses: number
  operacao: string
  manual: string
  pagina: string
  possuiRepeticao: boolean
}

type Pagina<T> = { itens?: T[]; totalItens?: number; totalRegistros?: number; pageSize?: number }

// ------------------------------------------------------------------ genéricos

/**
 * Lista o mestre. Aceita resposta em página ou array cru — as quatro APIs
 * discordam entre si nesse ponto, e a tela não precisa saber disso.
 */
export function usarMestre<T>(chave: string, rota: string, params: Record<string, unknown> = {}) {
  const q = useQuery({
    queryKey: ["outros", chave, params] as const,
    queryFn: () => obter<Pagina<T> | T[]>(rota, params),
  })

  const bruto = q.data
  const itens = Array.isArray(bruto) ? bruto : (bruto?.itens ?? [])
  const total = Array.isArray(bruto)
    ? bruto.length
    : (bruto?.totalItens ?? bruto?.totalRegistros ?? itens.length)

  return { ...q, itens: itens as T[], total }
}

/**
 * Todas as páginas de um mestre, para o seletor que precisa do acervo inteiro.
 *
 * A API entrega no máximo 200 por página e o acervo de textos de plano passa disso
 * (~216): um combo de `usarMestre` parava no 200º e o resto não se achava nem digitando,
 * porque a busca do seletor é local. Aqui a 1ª página diz o total, e as demais vêm de uma
 * vez. O tamanho de página usado para contar é o que a API **devolveu**, e não o pedido,
 * para não faltar página se ela aparar o pedido.
 */
export function usarMestreInteiro<T>(chave: string, rota: string, params: Record<string, unknown> = {}) {
  const q = useQuery({
    queryKey: ["outros", chave, "inteiro", params] as const,
    queryFn: async () => {
      const primeira = await obter<Pagina<T> | T[]>(rota, { ...params, pagina: 1 })
      if (Array.isArray(primeira)) return primeira
      const itens = primeira.itens ?? []
      const total = primeira.totalItens ?? primeira.totalRegistros ?? itens.length
      if (itens.length === 0 || itens.length >= total) return itens
      const paginas = Math.ceil(total / itens.length)
      const demais = await Promise.all(Array.from({ length: paginas - 1 }, (_, i) =>
        obter<Pagina<T>>(rota, { ...params, pagina: i + 2 })))
      return [...itens, ...demais.flatMap((p) => p.itens ?? [])]
    },
  })
  return { ...q, itens: (q.data ?? []) as T[] }
}

/** Uma coleção filha. Nenhuma delas pagina na API — ver a nota do módulo. */
export function usarFilhos<T>(chave: readonly unknown[], rota: string, ligado = true) {
  return useQuery({
    queryKey: ["outros-filhos", ...chave] as const,
    enabled: ligado,
    queryFn: () => obter<T[]>(rota, {}),
  })
}

/**
 * Inclui um registro de primeiro nível e devolve ele **relido da API**.
 *
 * Existe separado do `usarGravar` por causa do que vem de volta: os seis POST não
 * concordam. O torque devolve `{ id }`; tabela, plano e correias devolvem o objeto;
 * relés devolve outro formato ainda. E a tela abre o registro logo depois de criar —
 * torque sem etapa, plano sem veículo e tabela sem linha nascem inúteis — então ela
 * precisa do registro inteiro, com o que o servidor preencheu por conta dele.
 *
 * Reler é uma requisição a mais e uma fonte de verdade a menos: montar o objeto aqui
 * com os campos do formulário seria inventar o que o servidor decidiu.
 *
 * Se a criação não devolver id, isto **falha em voz alta**. O caminho silencioso seria
 * pior: a tela diria "criado" e não abriria nada, e ninguém saberia se gravou.
 */
export function usarIncluir<T>(invalidar: readonly unknown[][]) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ rota, dados }: { rota: string; dados: unknown }) => {
      const r = await api.post<{ id?: number; Id?: number }>(rota, dados, foraDoProjeto())
      const id = r.data?.id ?? r.data?.Id
      if (!id) throw new Error("a API criou o registro mas não devolveu o id")
      return await obter<T>(`${rota}/${id}`, {})
    },
    onSuccess: () => invalidar.forEach((k) => qc.invalidateQueries({ queryKey: k })),
  })
}

// ------------------------------------------------------------------ acervo de torques

const CHAVE_ACERVO = ["outros", "acervo-torques"] as const

/**
 * O acervo compartilhado de figuras de torque.
 *
 * Existe porque a mesma figura — a sequência de aperto de um cabeçote, por exemplo —
 * serve muitos motores: são **41 figuras associadas a 395 torques**. Cadastrar uma vez
 * e associar é o que evita subir a mesma imagem dezenas de vezes.
 */
export function usarAcervoDeTorques() {
  return useQuery({
    queryKey: CHAVE_ACERVO,
    queryFn: () => obter<FiguraDoAcervo[]>("api/torques-aperto/banco-imagens", {}),
  })
}

/**
 * Associa uma figura do acervo a um torque.
 *
 * **Manda `{ ordem }` no corpo, e isso não é opcional**: a rota declara
 * `[FromBody] AssociarImagemTorqueRequestDTO` e um POST sem corpo devolve 415 — medido
 * em 25/09/2026. Um 415 parece falha de rota e é falta de payload.
 */
export function usarAssociarDoAcervo(invalidar: readonly unknown[][]) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ torqueId, figuraId, ordem }:
                       { torqueId: number; figuraId: number; ordem: number }) => {
      await api.post(`api/torques-aperto/${torqueId}/imagens/banco/${figuraId}`,
                     { ordem }, foraDoProjeto())
    },
    onSuccess: () => invalidar.forEach((k) => qc.invalidateQueries({ queryKey: k })),
  })
}

/**
 * Cadastra uma figura NOVA no acervo, com o arquivo.
 *
 * A rota antiga (`POST banco-imagens`) recebe só o nome do arquivo em JSON — grava uma
 * linha apontando para um arquivo que precisa ter chegado por outro caminho. Esta manda
 * o binário, e por isso é a que a tela usa.
 */
export function usarSubirFiguraParaAcervo() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ arquivo, titulo }: { arquivo: File; titulo: string }) => {
      const corpo = new FormData()
      corpo.append("File", arquivo)
      if (titulo.trim()) corpo.append("Titulo", titulo.trim())
      // Sem `Content-Type`: o navegador põe o boundary do multipart.
      const r = await api.post<{ bancoImagemId: number; imagem: string }>(
        "api/torques-aperto/banco-imagens/arquivo", corpo, foraDoProjeto())
      return r.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: CHAVE_ACERVO }),
  })
}

/**
 * Tira a figura do acervo.
 *
 * Isto NÃO desfaz associação: é a figura saindo do acervo. A API apaga também o arquivo
 * pela ApiConteudo, então é decisão de verdade — o que sai não volta.
 */
export function usarRemoverDoAcervo() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (figuraId: number) => {
      await api.delete(`api/torques-aperto/banco-imagens/${figuraId}`, foraDoProjeto())
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: CHAVE_ACERVO }),
  })
}

/** Cria, altera ou apaga, invalidando o que a tela mostra. */
export function usarGravar(invalidar: readonly unknown[][]) {
  const qc = useQueryClient()
  const limpar = () => invalidar.forEach((k) => qc.invalidateQueries({ queryKey: k }))

  return {
    criar: useMutation({
      mutationFn: async ({ rota, dados }: { rota: string; dados: unknown }) => {
        const r = await api.post(rota, dados, foraDoProjeto())
        return r.data
      },
      onSuccess: limpar,
    }),
    alterar: useMutation({
      mutationFn: async ({ rota, dados }: { rota: string; dados: unknown }) => {
        await api.put(rota, dados, foraDoProjeto())
      },
      onSuccess: limpar,
    }),
    excluir: useMutation({
      mutationFn: async (rota: string) => {
        await api.delete(rota, foraDoProjeto())
      },
      onSuccess: limpar,
    }),
  }
}

/**
 * Põe no ar e tira do ar.
 *
 * **É rota própria, e não um campo do corpo do PUT**, e a razão está escrita na
 * própria API: publicar é o que faz o conteúdo aparecer para quem paga, e
 * cadastrar sem publicar deixa o trabalho pronto e invisível — sem nenhuma tela
 * dizendo isso. Um campo no meio de um formulário é exatamente o que se esquece
 * de marcar; um botão que diz o que vai acontecer, não.
 *
 * As quatro rotas têm a mesma forma — `POST {base}/{id}/publicar` e
 * `.../despublicar`, sem corpo, 204 — então um hook atende torques, relés,
 * planos e caixas de fusíveis.
 *
 * **Despublicar não apaga nada**: o cadastro continua, deixa de ser visível.
 */
export function usarPublicacao(invalidar: readonly unknown[][]) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ base, publicar }: { base: string; publicar: boolean }) => {
      await api.post(`${base}/${publicar ? "publicar" : "despublicar"}`, null, foraDoProjeto())
    },
    onSuccess: () => invalidar.forEach((k) => qc.invalidateQueries({ queryKey: k })),
  })
}

/**
 * Sobe uma imagem.
 *
 * Aqui a API **aceita binário** (`[FromForm]`), ao contrário do cadastro de
 * conteúdo, onde o campo de arquivos é só uma lista de nomes. Por isso torques e
 * relés têm upload de verdade e o conteúdo não.
 */
export function usarSubirImagem(invalidar: readonly unknown[][]) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ rota, arquivo, campo, extras }: {
      rota: string
      arquivo: File
      /**
       * O nome do campo no multipart, e ele **tem de casar com a propriedade do DTO**:
       * `File` no torque, `Imagem` nos relés. Nome errado devolve 400 antes mesmo de
       * chegar ao storage — o torque errou assim, e os relés também, com o antigo padrão
       * `arquivo` ("The Imagem field is required"). Por isso não há padrão.
       */
      campo: string
      /** Campos de texto que vão no mesmo formulário, como o título da figura. */
      extras?: Record<string, string>
    }) => {
      const corpo = new FormData()
      corpo.append(campo, arquivo)
      for (const [chave, valor] of Object.entries(extras ?? {})) {
        if (valor.trim()) corpo.append(chave, valor.trim())
      }
      // Sem `Content-Type` de propósito: o navegador precisa pôr o boundary do
      // multipart, e fixá-lo aqui quebraria o upload no servidor.
      await api.post(rota, corpo, foraDoProjeto())
    },
    onSuccess: () => invalidar.forEach((k) => qc.invalidateQueries({ queryKey: k })),
  })
}
